<script lang="ts">
export default {
    name: 'ManageAppointment'
}
</script>

<script setup lang="ts">
import AppointmentStatus from '@/components/manage-appointment/AppointmentStatus.vue'
import Option from '@/components/manage-appointment/Option.vue'
import { useAppointmentDateTime } from '@/components/manage-appointment/useAppointmentDateTime'
import IcalGenerator from '@/components/shared/IcalGenerator.vue'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { useToast } from '@/components/ui/toast/use-toast'
import { AppointmentStatusV2 } from '@/lib/status-v2'
import { getParam } from '@/lib/utils'
import { enableIntercom } from '@/store/features'
import { store } from '@/store/store'
import { usePublisher } from '@/utils/publisher'
import { show } from '@intercom/messenger-js-sdk'
import axios from 'axios'
import {
    CloudUpload,
    Loader2,
    MessageCircleQuestionMark,
    Pencil,
    PenLine,
    Plus,
    User,
    X
} from 'lucide-vue-next'
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
const freezableAppointmentMsg = "This appointment is locked because it starts soon. Reach out to support if you must reschedule or cancel.";

const { toast } = useToast()

const route = useRoute()
const router = useRouter()
const data = ref({} as any);
const loader = ref(true);
const cancelLoader = ref(false);
const patientId = ref('');
const publisher = usePublisher();
const { formattedDate, formattedTime } = useAppointmentDateTime(
    computed(() => data.value.day || ''),
    computed(() => data.value.time || '')
);


provide('manage_appointment:patient_id', patientId);

onMounted(() => {

    if (route.query.appID) {
        data.value = store.app;
        if (!data.value.msg || !data.value.time) {
            getAppt();
        } else {
            setInfo(data.value.time);
        }
    }

});

watch(() => store.members, function (val) {

    if (val.length && !store.app.appID && route.name == 'manage_appointment') {

        getAppt();

    }

});

async function getAppt() {
    try {
        const res = await axios.post('/getAppointment', { appID: route.query.appID }) as any;
        if (res.status == 'success') {
            const safeAppData = JSON.parse(JSON.stringify(res.data));
            data.value = safeAppData;
            if (!store.members || store.members.length === 0) {
                const membersRes = await axios.post('/getPatients', { pid: res.data.pid }) as any;

                if (membersRes.status !== 'success' || membersRes.data.length === 0) {
                    throw new Error('Failed to fetch list of members');
                }

                store.members = membersRes.data;
            }

            store.setBookedApp(safeAppData);
            setInfo(res.data.time);

            patientId.value = data.value.pid;
        } else {
            toast({
                title: res.msg ?? 'An error occurred while fetching your appointment info. Please reload the page.',
                variant: 'destructive'
            });
            loader.value = false;
        }
    } catch {
        toast({
            title: 'An error occurred while fetching your appointment info. Please reload the page.',
            variant: 'destructive'
        });
        loader.value = false;
    }
}

function setInfo(time: string) {
    data.value.time = time;
    loader.value = false;
}

function delAppointment() {
    if ((data.value.status_v2 as AppointmentStatusV2).meta.is_locked) {
        toast({
            title: freezableAppointmentMsg,
            variant: 'destructive'
        });
        return;
    }

    if (!cancelLoader.value) {
        cancelLoader.value = true;
        let values = {
            appID: route.query.appID || data.value.appID,
            pid: data.value.pid,
            doFrom: 'patient',
            save: true
        };
        publisher.publish('avee:click:appointment:cancel', {
            appointmentId: values.appID
        });

        axios.post('/delAppointment', values).then((res: any) => {
            if (res.status == 'success') {
                toast({
                    title: 'The appointment is cancelled.',
                });
                getAppt();

                publisher.publish('avee:appointment:cancel', {
                    appointmentId: values.appID
                });
            } else {
                toast({
                    title: 'Error in canceling appointment.',
                    variant: 'destructive'
                })
            }
        }).catch(() => {
            toast({
                title: 'Error in canceling appointment',
                variant: 'destructive'
            })
        }).finally(() => {
            cancelLoader.value = false;
        });
    }
}

function reschedule() {
    if ((data.value.status_v2 as AppointmentStatusV2).meta.is_locked) {
        toast({
            title: freezableAppointmentMsg,
            variant: 'destructive'
        });
        return;
    }

    router.push({
        name: 'patient_doctors',
        params: {
            domain: getParam(route),
            patientId: data.value.pid
        }
    });

    localStorage.setItem('reschedule', store.app.pid);

    publisher.publish('avee:click:appointment:reschedule', {
        appointmentId: route.query.appID || data.value.appID
    });
}

const shouldShowAddToCal = computed(() => {
    const statusInfo = data.value?.status_v2 as AppointmentStatusV2;

    return (
        (statusInfo.status === 'confirmed' || statusInfo.status === 'waiting_for_payment') &&
        !!data.value.doctor?.duration
    );
});

const shouldShowBookNew = computed(() => {
    const statusInfo = data.value?.status_v2 as AppointmentStatusV2;
    const bookNewStatuses = ['cancelled_by_patient', 'cancelled_by_doctor', 'reservation_expired', 'failed', 'no_show', 'visited'] as AppointmentStatusV2['status'][];
    return bookNewStatuses.includes(statusInfo.status);
});

const changeable = computed(() => {
    const statusInfo = data.value?.status_v2 as AppointmentStatusV2;
    const changeableStatuses = ['confirmed', 'waiting_for_payment'] as AppointmentStatusV2['status'][];
    return changeableStatuses.includes(statusInfo.status) && !statusInfo.meta.is_locked;
});

function redirectToUploadFiles() {
    router.push({
        name: 'upload',
        params: { domain: getParam(route) },
        query: { appID: data.value.appID }
    });
}

function redirectToFeedback() {
    router.push({
        name: 'feedback',
        query: { appID: route.query.appID }
    });
}
</script>

<template>
    <Loader2 v-if="loader" class="animate-spin w-10 h-10 mx-auto mt-10" />
    <div v-else class="mt-0 sm:mt-12">
        <div class="bg-white border border-gray-200 rounded-2xl shadow-sm">
            <div class="p-5 flex flex-wrap gap-4">
                <div class="font-extrabold text-[28px] w-full">
                    <span v-if="data.status_v2.meta.is_in_person">In-Person</span>
                    <span v-else>Phone</span>
                    <span> Visit</span>
                </div>
                <div class="flex items-center w-full">
                    <div class="text-sm text-gray-400 mr-1.5 font-medium">with</div>
                    <img v-if="data.doctor?.avatar" :src="data.doctor.avatar" :alt="data.doctor?.name" width="24"
                        height="24" class="size-6 object-cover rounded-md">
                    <div v-else class="border rounded-md border-gray-200 p-1">
                        <User class="size-4" />
                    </div>
                    <div class="ml-2 font-semibold text-gray-900">{{ data.doctor?.name }}</div>
                </div>
                <div class="flex items-center">
                    <div class="text-sm text-gray-400 mr-1.5 font-medium">at</div>
                    <div class="font-semibold text-gray-900">{{ formattedTime }}</div>
                </div>
                <div class="flex items-center">
                    <div class="text-sm text-gray-400 mr-1.5 font-medium">on</div>
                    <div class="font-semibold text-gray-900">{{ formattedDate }}</div>
                </div>
                <div class="flex items-center w-full">
                    <div class="text-sm text-gray-400 mr-1.5">for</div>
                    <div class="font-semibold text-gray-900">{{ data.patientName }}</div>
                </div>
            </div>
            <AppointmentStatus :status="data.status_v2" />
        </div>

        <div class="mt-7 grid gap-4 px-5 pb-12">
            <template v-if="shouldShowAddToCal && !!data?.status_v2">
                <IcalGenerator :doctor-name="data.doctor?.name" :start-time="new Date(data.status_v2.meta.before_visit_time)"
                    :end-time="new Date(data.status_v2.meta.after_visit_time)" :is-in-person="data.status_v2.meta.is_in_person" />
                <div class="border-b border-gray-200"></div>
            </template>

            <template v-if="(data.status_v2.status as AppointmentStatusV2['status']) === 'visited'">
                <Option title="Submit your feedback" subtitle="Help us improve our service."
                    @click="redirectToFeedback">
                    <PenLine class="size-5" />
                </Option>
                <div class="border-b border-gray-200"></div>
            </template>

            <template v-if="shouldShowBookNew">
                <Option title="Book new appointment" subtitle="If you want another appointment."
                    @click="router.push({ name: 'profiles', params: { domain: getParam(route) } })">
                    <Plus class="size-5" />
                </Option>
                <div class="border-b border-gray-200"></div>
            </template>

            <template v-if="changeable">
                <Option title="Upload files for doctor" subtitle="Upload images or documents for your visit."
                    @click="redirectToUploadFiles">
                    <CloudUpload class="size-5" />
                </Option>
                <div class="border-b border-gray-200"></div>
                <Option title="Reschedule appointment" subtitle="If this one doesn't work." @click="reschedule">
                    <Pencil class="size-5" />
                </Option>
                <div class="border-b border-gray-200"></div>
            </template>

            <AlertDialog>
                <AlertDialogTrigger as-child>
                    <Option v-if="changeable" title="Cancel appointment" subtitle="If you no longer need it."
                        :disabled="cancelLoader">
                        <Loader2 v-if="cancelLoader" class="size-5 animate-spin" />
                        <X v-else class="size-5" />
                    </Option>
                </AlertDialogTrigger>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to cancel this appointment?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            No, keep appointment
                        </AlertDialogCancel>
                        <AlertDialogAction @click="delAppointment">
                            Yes, cancel appointment
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <div v-if="changeable" class="border-b border-gray-200"></div>
            <Option v-if="enableIntercom" title="Chat with support" subtitle="If you need help." @click="show">
                <MessageCircleQuestionMark class="size-5" />
            </Option>
        </div>
    </div>
</template>
